import { Alert, Button, Drawer, Flex, Input, Select, Space, Switch, Typography } from 'antd'
import SettingsTitledCard from '../settings/SettingsTitledCard'
import SwitchWithDescription from '../common/SwitchWithDescription'
import { centsToDollarsDisplay } from '../displayFormatters'
import { useEffect, useState, useRef } from 'react'
import { faEnvelope, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import IconAndTextButton from '../IconAndTextButton'
import VSpace from '../common/VSpace'
import RichTextEditor from '../RichTextEditor/RichTextEditor'
import FollowUpSchedule from '../jobs/Proposals/Followup/FollowUpSchedule'
import { Tokens } from '../RichTextEditor/tokenModel'
import { EmailTemplateTypes } from '../../common/constants'
import { EmailTemplate } from '../EmailTemplates/emailTemplateModel'
import { useEmailTemplatesQuery } from '../EmailTemplates/emailTemplateQueries'
import { useProposalSettingsQuery } from '../../domain/proposals/settings/proposalSettingsQueries'
import { ProposalJobData } from '../jobs/Proposals/proposalsModel'
import { defaultEmailTemplate } from './proposalsModel'
import { useResendProposalMutation } from './proposalQueries'
import {
  canSend,
  initialSendState,
  parseEmailList,
  SendProposalState,
  sendValidationErrors
} from './sendProposalModel'
import './ManageProposalDrawer.css'

type Props = {
  job: ProposalJobData
  proposalId: number
  proposalTotal: number
  open: boolean
  onClose: () => void
}

const SendProposalDrawer = ({ job, proposalId, proposalTotal, open, onClose }: Props) => {
  const salesPersonEmail = job.salesPersonEmail
  const customerEmails = job.customer?.emails ?? []
  const settingsQuery = useProposalSettingsQuery()
  const emailTemplatesQuery = useEmailTemplatesQuery()

  const defaults = {
    template: defaultEmailTemplate,
    salesPersonEmail,
    sendProposalPdfByDefault: settingsQuery.data?.sendProposalPdfByDefault ?? false,
    notifySalesPersonByDefault: settingsQuery.data?.enableSalesNotificationsOnCustomerViewsByDefault ?? true
  }

  const [state, setState] = useState<SendProposalState>(initialSendState(defaults))
  const update = (changes: Partial<SendProposalState>) => setState(x => ({ ...x, ...changes }))

  const wasOpen = useRef(false)

  useEffect(() => {
    if (open && !wasOpen.current) {
      setState(initialSendState(defaults))
    }
    wasOpen.current = open
  }, [open, settingsQuery.data, salesPersonEmail])

  const resendMutation = useResendProposalMutation(() => {
    if (window && window.Intercom) {
      window.Intercom('trackEvent', 'sent-proposal', {
        job_name: job.name,
        date: job.estimateDate,
        salesperson: job.salesPersonName,
        customer: job.customer?.displayName ?? '',
        estimated_price: (proposalTotal / 100).toFixed(2)
      })
    }
    onClose()
  })

  const emailTemplates = [defaultEmailTemplate, ...emailTemplatesQuery.data ?? []]
  const emailTemplateOptions = emailTemplates
    .filter(x => x.type === EmailTemplateTypes.Proposal)
    .map(x => ({ label: x.name, value: x.guid }))

  const selectTemplate = (guid: string) => {
    const template: EmailTemplate = emailTemplates.find(x => x.guid === guid) ?? defaultEmailTemplate
    update({
      selectedEmailTemplate: template,
      emailSubject: template.emailSubject ?? '',
      emailBody: template.emailBody ?? ''
    })
  }

  const errors = sendValidationErrors(state)

  const send = () => {
    if (!canSend(state)) {
      return
    }
    resendMutation.mutate({
      projectId: job.id,
      proposalId,
      recipients: state.recipients.map(x => ({ address: x })),
      ccRecipients: parseEmailList(state.ccRecipients).map(x => ({ address: x })),
      emailReplyToAddresses: parseEmailList(state.replyToAddresses).map(x => ({ address: x })),
      emailSubject: state.emailSubject,
      emailBody: state.emailBody,
      sendPdf: state.sendPdf,
      notifySalesPersonOfCustomerViews: state.notifySalesPerson,
      followUpAutomation: state.automatedSchedule?.enabled ? state.automatedSchedule : null
    })
  }

  return <Drawer
    open={open}
    onClose={onClose}
    placement="bottom"
    height="95%"
    maskClosable={false}
    styles={{ header: { display: 'none' }, body: { padding: 0, background: '#f5f7fa' } }}
  >
    <div className="manage-proposal__header">
      <Typography.Text strong style={{ fontSize: '16px' }}>Send Proposal</Typography.Text>
      <Space size="large">
        <Typography.Text>
          <Typography.Text type="secondary">Final Price: </Typography.Text>
          <Typography.Text strong>{centsToDollarsDisplay(proposalTotal)}</Typography.Text>
        </Typography.Text>
        <Button
          type="text"
          aria-label="Close"
          icon={<FontAwesomeIcon icon={faXmark} />}
          onClick={onClose}
          disabled={resendMutation.isLoading}
        />
      </Space>
    </div>

    <div className="manage-proposal__body">
      <Flex vertical gap="large" className="manage-proposal__content">
        {errors.length > 0 && <Alert
          type="warning"
          showIcon
          message="Before this can be sent"
          description={<ul style={{ margin: 0, paddingLeft: '18px' }}>
            {errors.map(x => <li key={x}>{x}</li>)}
          </ul>}
        />}

        <SettingsTitledCard
          title="Details"
          description="Choose who receives this proposal and what the email says."
        >
          <VSpace size="large">
            <label className="manage-proposal__field" style={{ maxWidth: 'none' }}>
              <div>Recipient</div>
              <Select
                mode="multiple"
                value={state.recipients}
                onChange={value => update({ recipients: value })}
                placeholder="Select recipients"
                style={{ width: '100%' }}
                options={customerEmails.map(x => ({
                  label: x.isPrimary ? `${x.address} (primary)` : x.address,
                  value: x.address
                }))}
              />
            </label>

            <label className="manage-proposal__field">
              <div>CC</div>
              <Typography.Text type="secondary">
                Pick from the customer&rsquo;s addresses or type any other. They receive one copy and are not tracked as recipients.
              </Typography.Text>
              <Select
                mode="tags"
                value={parseEmailList(state.ccRecipients)}
                onChange={value => update({ ccRecipients: value.join(', ') })}
                placeholder="Select closed copy recipients"
                style={{ width: '100%' }}
                tokenSeparators={[',', ' ']}
                options={customerEmails.map(x => ({ label: x.address, value: x.address }))}
              />
            </label>

            <label className="manage-proposal__field">
              <div>Reply to</div>
              <Space>
                <Switch
                  checked={state.addReplyToAddress}
                  onChange={value => update({
                    addReplyToAddress: value,
                    replyToAddresses: state.replyToAddresses.length < 1 ? salesPersonEmail ?? '' : state.replyToAddresses
                  })}
                />
                <Input
                  value={state.replyToAddresses}
                  onChange={e => update({ replyToAddresses: e.target.value })}
                />
              </Space>
            </label>

            <VSpace>
              <div>Email Template</div>
              <Select
                options={emailTemplateOptions}
                value={state.selectedEmailTemplate.guid}
                style={{ width: '100%' }}
                onSelect={selectTemplate}
              />
            </VSpace>

            <VSpace>
              <div>Subject</div>
              <RichTextEditor value={state.emailSubject} onChange={x => update({ emailSubject: x })} singleLine tokens={Tokens} />
            </VSpace>

            <VSpace>
              <div>Body</div>
              <RichTextEditor value={state.emailBody} tokens={Tokens} onChange={x => update({ emailBody: x })} />
            </VSpace>
          </VSpace>
        </SettingsTitledCard>

        <SettingsTitledCard
          title="Settings"
          description="Options that apply to this send only."
        >
          <VSpace size="large">
            <SwitchWithDescription
              checked={state.sendPdf}
              label="Allow the customer to download a PDF copy of the proposal"
              description="Adds a download link to the proposal the customer receives."
              onClick={() => update({ sendPdf: !state.sendPdf })}
            />
            <SwitchWithDescription
              checked={state.notifySalesPerson}
              disabled={salesPersonEmail === undefined}
              label="Notify the sales person when the customer has viewed the proposal"
              description="Emails the salesperson the first time the customer opens it."
              onClick={() => update({ notifySalesPerson: !state.notifySalesPerson })}
            />
          </VSpace>
        </SettingsTitledCard>

        <SettingsTitledCard
          title="Follow-Up Emails"
          description="Automatic reminders sent if the customer has not responded."
        >
          <FollowUpSchedule
            onScheduleChange={(hasErrors, schedule) => {
              if (!hasErrors) {
                update({ automatedSchedule: schedule })
              }
            }}
            showDates={true}
          />
        </SettingsTitledCard>
      </Flex>
    </div>

    <div className="manage-proposal__footer">
      <Button onClick={onClose} disabled={resendMutation.isLoading}>Cancel</Button>
      <IconAndTextButton
        type="primary"
        icon={faEnvelope}
        text="Send for Review"
        onClick={send}
        loading={resendMutation.isLoading}
        disabled={!canSend(state) || resendMutation.isLoading}
      />
    </div>
  </Drawer>
}

export default SendProposalDrawer
